import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getAdminBillingTransactionDetail } from "../../api/admin.api";
import { isUnauthorizedError } from "../../api/http";
import AdminBreadcrumbs from "../../components/admin/AdminBreadcrumbs";
import EmptyState from "../../components/ui/EmptyState";
import ErrorState from "../../components/ui/ErrorState";
import LoadingState from "../../components/ui/LoadingState";
import AdminLayout from "../../layouts/AdminLayout";
import AdminBlockingModal from "./AdminBlockingModal";
import normalizeApiError from "../../utils/normalizeApiError";

const formatTimestamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const pad = (num) => String(num).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export default function AdminBillingDetail() {
  const { id } = useParams();
  const [transaction, setTransaction] = useState(null);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [blockingError, setBlockingError] = useState("");

  const fetchDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);
      const response = await getAdminBillingTransactionDetail(id);
      setTransaction(response || null);
    } catch (err) {
      if (isUnauthorizedError(err)) {
        setError(normalizeApiError(err));
        return;
      }
      const code = err?.response?.data?.code;
      if (code === "BILLING_CREDITS_EXHAUSTED") {
        setBlockingError(err?.response?.data?.message || "Credits exhausted.");
        return;
      }
      if (code === "INVALID_USER_ID") {
        setNotFound(true);
        return;
      }
      setError(normalizeApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const detailRows = useMemo(() => {
    if (!transaction) return [];
    return [
      { label: "ID", value: transaction.id },
      { label: "User ID", value: transaction.userId },
      { label: "Plan", value: transaction.planName || transaction.planId },
      {
        label: "Amount",
        value:
          typeof transaction.amount === "number"
            ? `${transaction.amount} ${transaction.currency || ""}`.trim()
            : "—",
      },
      { label: "Status", value: transaction.status },
      { label: "Provider", value: transaction.provider },
      { label: "Reference", value: transaction.reference },
      { label: "Created", value: formatTimestamp(transaction.createdAt) },
      { label: "Updated", value: formatTimestamp(transaction.updatedAt) },
    ];
  }, [transaction]);

  return (
    <AdminLayout title="Admin Billing Detail">
      <div className="space-y-4">
        <div className="card p-4">
          <AdminBreadcrumbs />
        </div>
        {loading ? (
          <LoadingState />
        ) : notFound ? (
          <EmptyState
            title="Transaction not found"
            message="This billing transaction is not available."
            testId="admin-empty-billing-detail"
          />
        ) : error ? (
          <ErrorState error={error} onRetry={fetchDetail} />
        ) : (
          <div className="card p-6 text-sm text-slate-600">
            {transaction ? (
              <div className="grid gap-3 md:grid-cols-2">
                {detailRows.map((row) => (
                  <div key={row.label} className="rounded-lg border border-slate-100 p-3">
                    <p className="text-xs text-slate-400">{row.label}</p>
                    <p className="mt-1 text-sm text-slate-900">
                      {row.value || "—"}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              "No transaction data."
            )}
          </div>
        )}
      </div>
      <AdminBlockingModal
        title="Billing credits exhausted"
        message={blockingError}
        onClose={() => setBlockingError("")}
      />
    </AdminLayout>
  );
}
